import { TreatmentPlanResponse } from "../services/treatmentPlanService";

const CASE_A_MAX_APPOINTMENTS = 5;
const CASE_A_MAX_BULLETS = 3;

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatFee(fee: number): string {
  return fee.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Auto-generates plain-text patient summary from treatment plan data.
 *
 * Case A (≤5 appointments): Appointment {N} — {timeframeLabel}, up to 3 step bullets
 * Case B (>5 appointments): Appointment {N} heading + one descriptive line
 *
 * The timeframeLabel used for appointment N comes from appointment N-1
 * (it represents the gap between N-1 and N).
 */
export function generateSummarizedContent(
  treatmentPlan: TreatmentPlanResponse,
): string {
  const sorted = [...treatmentPlan.appointments].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  const isSimpleCase = sorted.length <= CASE_A_MAX_APPOINTMENTS;
  const lines: string[] = [];

  if (treatmentPlan.treatmentSummary) {
    const intro = stripHtml(treatmentPlan.treatmentSummary);
    if (intro) {
      lines.push(intro, "");
    }
  }

  sorted.forEach((appt, index) => {
    const timeframeFromPrev =
      index > 0 ? sorted[index - 1].timeframeLabel : null;

    if (isSimpleCase) {
      const heading = timeframeFromPrev
        ? `Appointment ${index + 1} \u2014 ${timeframeFromPrev}`
        : `Appointment ${index + 1}`;
      lines.push(heading);

      const stepsToShow = [...appt.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .slice(0, CASE_A_MAX_BULLETS);

      stepsToShow.forEach((step) => {
        lines.push(`\u2022 ${step.name}`);
      });
    } else {
      const heading = timeframeFromPrev
        ? `Appointment ${index + 1} \u2014 ${timeframeFromPrev}`
        : `Appointment ${index + 1}`;
      lines.push(heading);

      if (appt.description) {
        lines.push(appt.description);
      }
    }

    lines.push("");
  });

  const hasOptions = sorted.some(
    (a) => a.conditionalPath && a.conditionalPath.trim() !== "",
  );
  if (!hasOptions && treatmentPlan.totalFee != null) {
    lines.push(`Total care cost: $${formatFee(treatmentPlan.totalFee)}`);
  }

  return lines.join("\n").trim();
}
